import React, { useRef, useState } from "react";
import { RCCBadge, RCCDropDownItem, RCCGrid, RCCHidden, RCCIcon, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import SortableHandle from "src/components/Sortable/Handle";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import formatNumber from "src/utils/formatNumber";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { simplePermissionType } from "src/components/Permissions/interface";
import { useAuth } from "src/context/Auth/context";
import swotLabels from "../../SWOT/SwotLabels";
import { IMatba001Form, IMatba004 } from "../interface";
import ModalAttachmentsAnalysis, { IRefModalAttachmentsAnalysis } from "./modalAttachments";
import ModalAddSwotAnalysis, { IRefModalAddSwotAnalysis } from "./modalAddSwot";
import { IRefSwotDetailModal } from "../../SWOT/components/SWOTItens/components/interface";
import SwotDetailModal from "../../SWOT/components/SWOTItens/components/ItemDetail";

export interface ILevel3List{
	analysisData: IMatba001Form;
	item: IMatba004;
	copyItem: (row:IMatba004) => void;
	changeItem: (row:IMatba004, change: boolean) => void;
	deleteItem: (row:IMatba004, useRequest:boolean) => void;
}

const Level3List: React.FC<ILevel3List> = ({
	analysisData,
	item,
	changeItem,
	copyItem,
	deleteItem
}) => {
	const { administrator, superUser, checkIfHasSpecificPermission } = useAuth();
	const { translate, currentLanguage } = useTranslation();
	const [showComment, setShowComment] = useState<boolean>(false);
	const {importance,intensity,tendencyAnalysis} = swotLabels(translate);	

	const refModalDetailItem = useRef<IRefSwotDetailModal>(null);
	const refAttachments = useRef<IRefModalAttachmentsAnalysis>(null);
	const refAddSwot = useRef<IRefModalAddSwotAnalysis>(null);

	const isAdminOrSuper = administrator || superUser;
	const canCreateUpdate = checkIfHasSpecificPermission("/analysis/panel", PermissionEnum.CREATE_UPDATE);
	const canEditAttachments = isAdminOrSuper || canCreateUpdate;

	const colOptions = (row: IMatba004) => (
		<OptionsColumn>
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				{item.metbs006 !== null && item.metbs006?.mes006Mes002Id ?
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							refModalDetailItem.current?.actionOpenModal(item.metbs006?.mes006Mes002Id || 0, 0);
						}}
					>
						<RCCIcon 
							name="linkExternal"
						/>
						{translate("Ver Item na SWOT")} 
					</RCCDropDownItem>
					: null
				}

				{analysisData.userPermission > Number(simplePermissionType.VIEW) &&
					<>
						{item.metbs006 === null || !item.metbs006?.mes006Mes002Id ?
							<RCCDropDownItem 
								selfEnclose
								onClick={() => {
									refAddSwot.current?.openModal(row, analysisData.maa001Label3, changeItem);
								}}
							>
								<RCCIcon 
									name="plus"
								/>
								{translate("Criar Item SWOT")} 
							</RCCDropDownItem>
							: null
						}
						<RCCDropDownItem 
							selfEnclose
							onClick={() => {copyItem(row); }}
						>
							<RCCIcon 
								name="copy" 
							/>
							{translate("Copiar")}
						</RCCDropDownItem>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => {changeItem(row, true);}}
						>
							<RCCIcon 
								name="pencil"
							/>
							{translate("Alterar")}
						</RCCDropDownItem>
					</>
				}

				
			</CheckPermission>
			{
				analysisData.userPermission > Number(simplePermissionType.VIEW) && 
				<>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => {deleteItem(row, true);}}
						>
							<RCCIcon 
								name="trash"
							/>
							{translate("Excluir")}
						</RCCDropDownItem>
					</CheckPermission>
				</>
			}
			
		</OptionsColumn>
	);

	const optionsList = (row: any) => (
		<>
			<RCCGrid
				container 
				item
				justifyContent="flex-end"
				xs={12}
			>
				<RCCTooltip
					text={translate("Anexos {{label}}", {label: analysisData.maa001Label3})}
					color="grey"
				>
					<RCCIcon
						style={{margin: 5, cursor: "pointer"}}
						name="attachment"
						color="grey"
						onClick={() => refAttachments.current?.openAttachments(row, analysisData.maa001Label3, !canEditAttachments)}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Comentários {{label}}", {label: analysisData.maa001Label3})}
					color="grey"
				>
					<RCCIcon
						style={{margin: 5, cursor: "pointer"}}
						name={item.maa004Comentario === "" ? "comment" : "commentFilled"}
						color="grey"
						onClick={() => setShowComment(!showComment)}
					/>
				</RCCTooltip>
				{
					analysisData.userPermission > Number(simplePermissionType.VIEW) && 
					<>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<SortableHandle>
								<RCCTooltip 
									color="grey"
									text={translate("Mover {{label}}", {label: analysisData.maa001Label3})}
								>
									<RCCIcon
										style={{margin: 5, cursor: "pointer"}}
										name="arrows"
										color="grey"
									/>
								</RCCTooltip>	
							</SortableHandle>
						</CheckPermission>
					</>
				}

				{colOptions(row)}				
			</RCCGrid>
		</>
	);

	const SwotLabel = () => {
		if(item.maa004Forca === "S"){
			return translate("Força");
		}

		if(item.maa004Fraqueza === "S"){
			return translate("Fraqueza");
		}

		if(item.maa004Oportunidade === "S"){
			return translate("Oportunidade");
		}

		if(item.maa004Ameaca === "S"){
			return translate("Ameaça");
		}

		return "";
	};

	return(
		<>
			<RCCGrid container>
				<RCCGrid container item xs={12}>
					{
						analysisData.hasSwot ?
							<RCCGrid item container
								xs={12}
								sm={analysisData.maa001Gut === "S" ? 2 : 5}
							>
								<span>
									<RCCHidden smUp><b>{translate("Descrição")}:</b> </RCCHidden>
									{item.maa004Descricao}
								</span>
							</RCCGrid>
							:
							<RCCGrid item container
								xs={12}
								sm={analysisData.maa001Gut === "S" ? 4 : 5}
							>
								<span>
									<RCCHidden smUp><b>{translate("Descrição")}:</b> </RCCHidden>
									{item.maa004Descricao}
								</span>
							</RCCGrid>
					}
					{
						analysisData.hasSwot &&
						<RCCGrid item container
							xs={12}
							sm={analysisData.maa001Gut === "S" ? 2 : 6}
						>
							<RCCHidden smUp><b>{translate("SWOT")}:</b> </RCCHidden> {SwotLabel()}
						</RCCGrid> 
					}
					{
						analysisData.maa001Gut === "S" &&
						<>
							<RCCGrid item container
								xs={12}
								sm={2}
							>
								<span>
									<RCCHidden smUp><b>{translate("Importância")}:</b> </RCCHidden>{importance[item.maa004Importancia-1] && importance[item.maa004Importancia-1].label}
								</span>
							</RCCGrid>
							<RCCGrid item container
								xs={12}
								sm={2}
							>
								<span><RCCHidden smUp><b>{translate("Intensidade")}:</b> </RCCHidden>{intensity[item.maa004Probabilidade-1] && intensity[item.maa004Probabilidade-1].label}</span>
							</RCCGrid>
							<RCCGrid item container
								xs={12}
								sm={2}
							>
								<span>
									<RCCHidden smUp><b>{translate("Tendência")}:</b> </RCCHidden>{tendencyAnalysis[item.maa004Tendencia-1] && tendencyAnalysis[item.maa004Tendencia-1].label}
								</span>
							</RCCGrid>
							<RCCGrid item container
								xs={12}
								sm={1}
							>
								<RCCGrid item container sm={6}>
									<RCCHidden smUp><b>{translate("Pontos")}:</b> </RCCHidden>
									{item.maa004Pontuacao}
								</RCCGrid>
								{
									analysisData.maa001Graficoradar === "S" &&
									<RCCGrid item container sm={6} title={`${translate("Avaliação Gráfico")}  (G x U x T) / 25`}>
										<RCCHidden smUp><b>{translate("Gráfico")}:</b> </RCCHidden>
										<RCCBadge color="blue"
											styleInline="font-size: 0.83rem; height: 15px"
										>
											{formatNumber(item.maa004Pontuacao / 25, currentLanguage)}
										</RCCBadge>
									</RCCGrid>
								}
								
							</RCCGrid>
						</>
					}
					<RCCGrid item container
						xs={12}
						sm={1}
						alignItems="flex-end"
					>
						{optionsList(item)}
					</RCCGrid>
				</RCCGrid>
				{
					showComment &&
					<RCCGrid container item xs={12}>
						<b>{`${translate("Comentário")}:`}</b> {item.maa004Comentario}
					</RCCGrid>
				}
			</RCCGrid>

			<ModalAttachmentsAnalysis ref={refAttachments} />
			<ModalAddSwotAnalysis ref={refAddSwot} />
			<SwotDetailModal ref={refModalDetailItem}/>
		</>
	);
};

export default Level3List;
